import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Upload, X, Image, Calendar, Loader2, Star } from "lucide-react";

const ProfessorFeedback = ({ 
  open, 
  onOpenChange, 
  student, 
  onSuccess,
  lang = "pt",
  initialLessonId = null  // Pre-select a specific lesson when opening
}) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectedLessonId, setSelectedLessonId] = useState("");
  const [pastLessons, setPastLessons] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [rating, setRating] = useState(0);
  const [improvementTips, setImprovementTips] = useState("");

  useEffect(() => {
    if (open && student) {
      fetchPastLessons();
      // Reset form
      setTitle("");
      setContent("");
      setSelectedLessonId(initialLessonId || "");
      setPhotos([]);
      setPhotoPreview([]);
      setRating(0);
      setImprovementTips("");
    }
  }, [open, student, initialLessonId]);

  const fetchPastLessons = async () => {
    if (!student?.user_id) return;
    setLoadingLessons(true);
    try {
      const res = await axios.get(`${API}/feedback/lessons/${student.user_id}`, { withCredentials: true });
      setPastLessons(res.data.lessons || []);
    } catch (err) {
      console.error("Failed to fetch past lessons:", err);
    } finally {
      setLoadingLessons(false);
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photos.length > 5) {
      toast.error(lang === "en" ? "Maximum 5 photos allowed" : "Máximo 5 fotos permitidas");
      return;
    }
    
    const newPhotos = [...photos, ...files];
    setPhotos(newPhotos);
    
    // Create previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPhotoPreview(prev => [...prev, ...newPreviews]);
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    const newPreviews = photoPreview.filter((_, i) => i !== index);
    setPhotos(newPhotos);
    setPhotoPreview(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error(lang === "en" ? "Please fill the content" : "Preencha o conteúdo");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("user_id", student.user_id);
      formData.append("content", content);
      if (title.trim()) {
        formData.append("title", title);
      }
      if (selectedLessonId) {
        formData.append("lesson_id", selectedLessonId);
      }
      if (rating > 0) {
        formData.append("rating", rating);
      }
      if (improvementTips.trim()) {
        formData.append("improvement_tips", improvementTips);
      }
      photos.forEach(photo => {
        formData.append("photos", photo);
      });

      await axios.post(`${API}/feedback/`, formData, {
        withCredentials: true,
      });

      toast.success(lang === "en" ? "Feedback sent successfully!" : "Feedback enviado com sucesso!");
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Failed to send feedback:", err);
      toast.error(err.response?.data?.detail || (lang === "en" ? "Failed to send feedback" : "Erro ao enviar feedback"));
    } finally {
      setLoading(false);
    }
  };

  if (!student) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={student.picture} />
              <AvatarFallback className="bg-[#6697a4] text-white">
                {student.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-base font-semibold">{lang === "en" ? "Give Feedback" : "Dar Feedback"}</p>
              <p className="text-sm text-[#94A3B8] font-normal">{student.name}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title (optional now) */}
          <div className="space-y-2">
            <Label htmlFor="feedback-title">
              {lang === "en" ? "Title (optional)" : "Título (opcional)"}
            </Label>
            <Input
              id="feedback-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={lang === "en" ? "e.g., Great progress today!" : "ex: Excelente progresso hoje!"}
              data-testid="feedback-title-input"
            />
          </div>

          {/* Link to Lesson (optional) */}
          <div className="space-y-2">
            <Label>
              <Calendar className="h-4 w-4 inline mr-1" />
              {lang === "en" ? "Link to lesson (optional)" : "Associar a aula (opcional)"}
            </Label>
            {loadingLessons ? (
              <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                <Loader2 className="h-4 w-4 animate-spin" />
                {lang === "en" ? "Loading lessons..." : "A carregar aulas..."}
              </div>
            ) : (
              <Select value={selectedLessonId} onValueChange={(val) => setSelectedLessonId(val === "none" ? "" : val)}>
                <SelectTrigger data-testid="feedback-lesson-select">
                  <SelectValue placeholder={lang === "en" ? "General feedback (no specific lesson)" : "Feedback geral (sem aula específica)"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {lang === "en" ? "General feedback" : "Feedback geral"}
                  </SelectItem>
                  {pastLessons.map(lesson => (
                    <SelectItem key={lesson.lesson_id} value={lesson.lesson_id}>
                      {lesson.date} - {lesson.title} ({lesson.start_time})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {pastLessons.length === 0 && !loadingLessons && (
              <p className="text-xs text-[#94A3B8]">
                {lang === "en" 
                  ? "No attended lessons found for this student" 
                  : "Nenhuma aula frequentada encontrada para este aluno"}
              </p>
            )}
          </div>

          {/* Rating (optional) */}
          <div className="space-y-2">
            <Label>{lang === "en" ? "Rating (optional)" : "Avaliação (opcional)"}</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(rating === star ? 0 : star)}
                  className="p-1 hover:scale-110 transition-transform"
                  data-testid={`feedback-rating-${star}`}
                >
                  <Star className={`h-6 w-6 ${star <= rating ? "text-[#F59E0B] fill-current" : "text-gray-300"}`} />
                </button>
              ))}
              {rating > 0 && (
                <button
                  type="button"
                  onClick={() => setRating(0)}
                  className="ml-2 text-xs text-[#94A3B8] hover:text-[#6697a4]"
                >
                  {lang === "en" ? "Clear" : "Limpar"}
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="feedback-content">
              {lang === "en" ? "Feedback" : "Feedback"} *
            </Label>
            <Textarea
              id="feedback-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={lang === "en" 
                ? "Write your feedback about the student's progress, technique, areas for improvement..." 
                : "Escreve o teu feedback sobre o progresso do aluno, técnica, áreas a melhorar..."}
              rows={4}
              data-testid="feedback-content-input"
            />
          </div>

          {/* Improvement Tips (optional) */}
          <div className="space-y-2">
            <Label htmlFor="improvement-tips">
              {lang === "en" ? "Improvement Tips (optional)" : "Dicas de Melhoria (opcional)"}
            </Label>
            <Textarea
              id="improvement-tips"
              value={improvementTips}
              onChange={(e) => setImprovementTips(e.target.value)}
              placeholder={lang === "en" 
                ? "Specific tips for the student to work on..." 
                : "Dicas específicas para o aluno trabalhar..."}
              rows={3}
              data-testid="feedback-improvement-tips-input"
            />
          </div>

          {/* Photos */}
          <div className="space-y-2">
            <Label>
              <Image className="h-4 w-4 inline mr-1" />
              {lang === "en" ? "Photos (optional, max 5)" : "Fotos (opcional, máx 5)"}
            </Label>
            
            {/* Photo previews */}
            {photoPreview.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {photoPreview.map((preview, idx) => (
                  <div key={`photo-preview-${idx}`} className="relative w-20 h-20 rounded-lg overflow-hidden bg-slate-100">
                    <img src={preview} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {photos.length < 5 && (
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-[#475569] transition-colors">
                    <Upload className="h-4 w-4" />
                    {lang === "en" ? "Add photos" : "Adicionar fotos"}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoChange}
                    className="hidden"
                    data-testid="feedback-photo-input"
                  />
                </label>
                <span className="text-xs text-[#94A3B8]">{photos.length}/5</span>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {lang === "en" ? "Cancel" : "Cancelar"}
            </Button>
            <Button
              type="submit"
              disabled={loading || !title.trim() || !content.trim()}
              className="bg-[#6697a4] hover:bg-[#3d5a63]"
              data-testid="feedback-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {lang === "en" ? "Sending..." : "A enviar..."}
                </>
              ) : (
                lang === "en" ? "Send Feedback" : "Enviar Feedback"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfessorFeedback;
